import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPortalResult } from '@whinerp/client';
import { Button, PortalField, PortalShell, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { portalFieldLabel, portalShellLabels } from './portal-labels.js';
import { defineScreen } from '../types.js';

/**
 * SC-37 Preference centre.
 *
 * PortalShell with preference content. No navigation, no workspace switcher,
 * nothing that belongs to somebody signed in - this page is opened by a person
 * who is not a user of this product, from a link in an email footer.
 *
 * The unsubscribe-from-everything control is genuinely one click and is not
 * behind a confirmation. A preference maze produces spam complaints instead of
 * opt-outs, and a complaint costs the whole workspace's sending reputation:
 * the expensive mistake here is making it too hard, not too easy.
 */

type Portal = OpenPortalResult;

function PreferenceCentreScreen(): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const params = useParams();
  const token = params['token'] ?? '';
  const [stopped, setStopped] = useState(false);

  const page = useQuery<Portal, ApiError>({
    queryKey: ['portal', 'preferences', token],
    queryFn: () => api.openPortal({ path: { surface: 'preferences' }, query: { token } }),
  });

  const setPreference = useMutation({
    mutationFn: (input: { channel: 'email' | 'sms' | 'whatsapp' | 'call'; optedIn: boolean }) =>
      api.setPortalPreference({ body: { token, ...input } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['portal', 'preferences', token] }),
  });

  const stopEverything = useMutation({
    mutationFn: () => api.unsubscribeFromEverything({ body: { token } }),
    onSuccess: () => {
      setStopped(true);
      void queryClient.invalidateQueries({ queryKey: ['portal', 'preferences', token] });
    },
  });

  if (page.isPending) return <PreferencesLoading />;

  /*
   * A failed request is a refusal page, not an empty content pane.
   *
   * Without this the shell rendered its content region with no data in it, and
   * a test asserting the region exists passed while the API was answering 500.
   * A customer would have seen a branded page with nothing on it and no way to
   * tell whether that meant "you have nothing" or "this is broken".
   */
  if (page.isError || !page.data) return <PreferencesError />;

  const data = page.data;
  const labels = portalShellLabels(t, data?.branding?.workspaceName ?? null);

  return (
    <PortalShell
      surface="preferences"
      workspaceName={data?.branding?.workspaceName ?? null}
      refusal={data?.refusal ?? null}
      labels={labels}
      /* An expired link offers a new one. The alternative to a working
         unsubscribe is a spam complaint. */
      onRequestNewLink={() => setStopped(false)}
    >
      <h2 className="text-lead text-ink">{t('preferences.title')}</h2>
      <p className="text-body text-ink-meta">{t('preferences.intro')}</p>

      {(data?.contact ?? []).map((field) => (
        <PortalField
          key={field.key}
          data-portal-field={field.key}
          label={portalFieldLabel(t, field.key)}
          value={field.value}
        />
      ))}

      <ul data-preferences className="flex flex-col gap-2">
        {(data?.preferences ?? []).map((preference) => (
          <li key={preference.channel} className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-body text-ink">
              <input
                type="checkbox"
                data-preference={preference.channel}
                checked={preference.optedIn}
                onChange={(event) =>
                  setPreference.mutate({
                    channel: preference.channel as 'email',
                    optedIn: event.target.checked,
                  })
                }
                className="h-icon w-icon rounded-control border border-control"
              />
              {t(`preferences.channel.${preference.channel}` as 'preferences.channel.email')}
            </label>
            {/* "Never asked" is not "you said no", and the difference is what
                tells somebody whether they ever chose this. */}
            {preference.status === 'unknown' ? (
              <span data-never-asked={preference.channel} className="text-meta text-ink-meta">
                {t('preferences.neverAsked')}
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      {stopped ? (
        <p role="status" data-stopped className="rounded-card bg-success-tint px-3 py-2 text-dense text-success-text">
          {t('preferences.stopped')}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {/* One click. No confirmation dialog: the expensive mistake here is
              making this too hard, not too easy. */}
          <Button
            intent="secondary"
            data-stop-everything
            busy={stopEverything.isPending}
            onClick={() => stopEverything.mutate()}
          >
            {t('preferences.stopAll')}
          </Button>
          <span className="text-meta text-ink-meta">{t('preferences.stopAllHelp')}</span>
        </div>
      )}
    </PortalShell>
  );
}

function PreferencesEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('portal.noQuotes')}</p>;
}

function PreferencesLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-canvas px-gutter py-6" role="status" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <div className="flex w-full max-w-reading flex-col gap-3">
        <Skeleton className="h-line-h2 w-panel-wide" />
        <Skeleton className="h-plot w-full" />
      </div>
    </div>
  );
}

function PreferencesError(): JSX.Element {
  const t = useT();
  /*
   * No ErrorState here, deliberately.
   *
   * ErrorState is internal chrome with a retry button and a support tone. A
   * customer who followed a link from an email footer gets the shell's own
   * refusal page, which says which of the three things happened and offers a
   * new link only when that makes sense.
   */
  return (
    <PortalShell
      surface="preferences"
      workspaceName={null}
      refusal={{ kind: 'invalid', canRequestNewLink: false }}
      labels={portalShellLabels(t, null)}
    >
      <span />
    </PortalShell>
  );
}

export const screen = defineScreen({
  id: 'SC-37',
  route: '/preferences/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Your preferences',
  features: ['F-081'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: ['PortalShell'],
  elements: ['PortalShell', 'PortalField'],
  // A real link, so every check audits the page a customer actually
  // opens rather than its refusal state.
  samplePortalToken: { surface: 'preferences', param: 'token' },
  states: { empty: PreferencesEmpty, loading: PreferencesLoading, error: PreferencesError },
  component: PreferenceCentreScreen,
});

export default screen;
